/**
 * CallNotificationPanel Component (V1104: Polling Architecture)
 *
 * Replaces LiveCallPanel with polling-based notifications.
 * Each call has one notification item that transitions through statuses:
 * INCOMING → IN_PROGRESS → ENDED → ANALYZING → ANALYZED / SKIPPED / FAILED
 */

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ClickablePhoneNumber from "@/components/common/ClickablePhoneNumber";
import {
  Phone,
  PhoneOff,
  CheckCircle,
  XCircle,
  ChevronDown,
  Bell,
  BellOff,
  Loader2,
  SkipForward,
  CheckCheck,
} from "lucide-react";
import { useCallNotifications } from "@/hooks/useCallNotifications";
import { useAuthStore } from "@/stores/authStore";
import type { CallNotificationDto } from "@/api/call-notification";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

// Phone number formatting
function formatPhoneNumber(phone: string | null): string {
  if (!phone) return "알 수 없음";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

// Duration formatting
function formatDuration(seconds: number | null): string {
  if (seconds == null) return "";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Time formatting
function formatTime(isoString: string | null): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

// Status icon
function getStatusIcon(status: string) {
  switch (status) {
    case "INCOMING":
      return <Phone className="h-4 w-4 text-info animate-pulse" />;
    case "IN_PROGRESS":
      return <Phone className="h-4 w-4 text-success" />;
    case "ENDED":
      return <PhoneOff className="h-4 w-4 text-muted-foreground" />;
    case "ANALYZING":
      return <Loader2 className="h-4 w-4 text-info animate-spin" />;
    case "ANALYZED":
      return <CheckCircle className="h-4 w-4 text-success" />;
    case "SKIPPED":
      return <SkipForward className="h-4 w-4 text-amber-500" />;
    case "FAILED":
      return <XCircle className="h-4 w-4 text-destructive" />;
    default:
      return <Bell className="h-4 w-4 text-muted-foreground" />;
  }
}

// Status label
function getStatusLabel(status: string): string {
  switch (status) {
    case "INCOMING":
      return "수신 중";
    case "IN_PROGRESS":
      return "통화 중";
    case "ENDED":
      return "통화 종료";
    case "ANALYZING":
      return "분석 중";
    case "ANALYZED":
      return "분석 완료";
    case "SKIPPED":
      return "짧은 통화";
    case "FAILED":
      return "분석 실패";
    default:
      return status;
  }
}

// Status background color
function getStatusBg(status: string): string {
  switch (status) {
    case "INCOMING":
    case "IN_PROGRESS":
      return "bg-info/10 border-l-4 border-l-info";
    case "ANALYZING":
      return "bg-info/5 border-l-4 border-l-info/50";
    case "ANALYZED":
      return "bg-success/10 border-l-4 border-l-success";
    case "SKIPPED":
      return "bg-amber-50 border-l-4 border-l-amber-500";
    case "FAILED":
      return "bg-destructive/10 border-l-4 border-l-destructive";
    default:
      return "hover:bg-muted/50";
  }
}

// Web Audio API notification sound
function createNotificationSound(): () => void {
  let audioContext: AudioContext | null = null;

  return () => {
    try {
      if (!audioContext) {
        audioContext = new (
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext
        )();
      }

      const now = audioContext.currentTime;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 880;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.02);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.15);
      oscillator.start(now);
      oscillator.stop(now + 0.15);
    } catch {
      // Audio API not available
    }
  };
}

const playBeepSound = createNotificationSound();

interface CallNotificationPanelProps {
  enableSound?: boolean;
}

export function CallNotificationPanel({
  enableSound = true,
}: CallNotificationPanelProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(enableSound);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  // Organization ID from user context
  const organizationId = user?.organization?.id;

  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useCallNotifications({
      organizationId,
      enabled: isAuthenticated,
    });

  // Play sound on new INCOMING notifications
  const [prevCount, setPrevCount] = useState(0);
  useEffect(() => {
    if (
      notifications.length > prevCount &&
      soundEnabled &&
      notifications[0]?.status === "INCOMING"
    ) {
      playBeepSound();
    }
    setPrevCount(notifications.length);
  }, [notifications.length, soundEnabled, notifications, prevCount]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleNotificationClick = useCallback(
    (notification: CallNotificationDto) => {
      // Mark as read (V1224+: userId는 서버 JWT에서 자동 추출)
      if (!notification.isRead) {
        markAsRead(notification.id);
      }
      // Navigate to consult detail
      if (notification.consultId) {
        navigate(`/consults/${notification.consultId}`);
      }
    },
    [navigate, markAsRead],
  );

  const handleMarkAllAsRead = useCallback(() => {
    if (organizationId) {
      markAllAsRead();
    }
  }, [markAllAsRead, organizationId]);

  if (!isAuthenticated || !organizationId) {
    return null;
  }

  // Active calls (INCOMING or IN_PROGRESS)
  const activeCalls = notifications.filter(
    (n) => n.status === "INCOMING" || n.status === "IN_PROGRESS",
  );
  const otherNotifications = notifications.filter(
    (n) => n.status !== "INCOMING" && n.status !== "IN_PROGRESS",
  );

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-[200] flex flex-col",
        "bg-background border rounded-lg shadow-lg",
        "transition-all duration-300 ease-in-out",
        isExpanded ? "w-80" : "w-auto",
      )}
    >
      {/* Collapsed state */}
      {!isExpanded && (
        <button
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-2 cursor-pointer rounded-lg",
            "bg-muted/30",
          )}
          onClick={toggleExpanded}
        >
          <div className="relative">
            <Bell className="h-4 w-4 text-foreground" />
            <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-success animate-pulse" />
          </div>
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="h-4 px-1 text-[10px] min-w-0"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </button>
      )}

      {/* Expanded state */}
      {isExpanded && (
        <>
          {/* Header */}
          <div
            className={cn(
              "flex items-center justify-between p-3 cursor-pointer",
              "border-b rounded-t-lg bg-muted/30",
            )}
            onClick={toggleExpanded}
          >
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">실시간 알림</span>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setSoundEnabled((prev) => !prev);
                }}
              >
                {soundEnabled ? (
                  <Bell className="h-3.5 w-3.5" />
                ) : (
                  <BellOff className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </Button>
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/20">
            <span className="text-xs text-muted-foreground">
              {activeCalls.length > 0 && `통화 중 ${activeCalls.length}건`}
              {activeCalls.length > 0 && otherNotifications.length > 0 && " · "}
              {otherNotifications.length > 0 &&
                `알림 ${otherNotifications.length}건`}
              {notifications.length === 0 && "알림 없음"}
            </span>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAllAsRead();
                }}
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                모두 읽음
              </Button>
            )}
          </div>

          {/* Notification list */}
          <ScrollArea className="max-h-[350px]">
            {notifications.length > 0 ? (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={cn(
                      "flex items-start gap-3 p-3 cursor-pointer transition-colors",
                      getStatusBg(notification.status),
                      !notification.isRead && "font-medium",
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Status icon */}
                    <div className="mt-0.5 shrink-0">
                      {getStatusIcon(notification.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium truncate inline-flex items-center gap-1">
                          {getStatusLabel(notification.status)}{" "}
                          <ClickablePhoneNumber
                            phone={notification.callerNumber}
                            className="text-xs font-medium"
                            showIcon={false}
                          />
                        </span>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {formatTime(notification.callStartedAt)}
                        </span>
                      </div>

                      {/* Customer name */}
                      {notification.customerName && (
                        <p className="text-xs text-foreground/80 truncate">
                          {notification.customerName}
                        </p>
                      )}

                      {/* Duration (for ended/analyzing/analyzed) */}
                      {notification.durationSeconds != null &&
                        notification.status !== "INCOMING" &&
                        notification.status !== "IN_PROGRESS" && (
                          <p className="text-[11px] text-muted-foreground">
                            {formatTime(notification.callStartedAt)} ~{" "}
                            {formatTime(notification.callEndedAt)} (
                            {formatDuration(notification.durationSeconds)})
                          </p>
                        )}

                      {/* Analysis summary */}
                      {notification.analysisSummary && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {notification.analysisSummary}
                        </p>
                      )}

                      {/* Skipped reason */}
                      {notification.status === "SKIPPED" &&
                        !notification.analysisSummary && (
                          <p className="text-xs text-amber-600 mt-0.5">
                            통화 시간 부족으로 분석 스킵
                          </p>
                        )}
                    </div>

                    {/* Unread indicator */}
                    {!notification.isRead && (
                      <div className="w-2 h-2 rounded-full bg-info shrink-0 mt-1.5" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Bell className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">알림이 없습니다</p>
                <p className="text-xs mt-1">새 전화가 오면 여기에 표시됩니다</p>
              </div>
            )}
          </ScrollArea>
        </>
      )}
    </div>
  );
}

export default CallNotificationPanel;
